<template>
    <div
    ref="headerRef"
    class="feature-header-base-wrapper"
    :style="wrapperStyles"
    :class="{
        'mobile': getMobileAppState,
        'non-authenticated-feature': nonAuthenticatedFeature,
        'mobile-status-bar-android': getDeviceInUse === 'android',
        'mobile-status-bar-ios': getDeviceInUse === 'ios'
    }">
        <!-- NonAuthenticatedLogo -->
        <component
        v-if="nonAuthenticatedFeature && !getMobileAppState"
        :is="logoBaiComponent"
        class="logo-bai"
        :class="{'mobile': getMobileAppState, 'center-logo': centerLogo}"/>
        <!-- Ghost div to help get mobile status bar size -->
        <div
        ref="helperForStatusBarHeightRef"
        class="to-help-get-ios-status-bar-height"
        :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }"/>
        <!-- Mobile Back button -->
        <button-tertiary
        v-if="(getMobileAppState || nonAuthenticatedFeature) && !hideClose"
        buttonStyle="round"
        buttonSize="small"
        :icon="nonAuthenticatedFeature ? 'close' : (customMobileBackIcon || 'arrow-left-full')"
        :class="{'right-position': nonAuthenticatedFeature, 'mobile': getMobileAppState}"
        @clicked="emit('backOrCloseClicked')"/>
        <!-- Slot for top right mobile button -->
        <slot v-if="showTopRightButtonMobileSlot" name="top-right-button-mobile-slot"></slot>
        <!-- *********************************************
        *                                                *
        *            Position Fixed top Elements         *
        *                     Curtain                	 *
        *                                                *
        ********************************************** -->
        <div class="curtain" :style="{'height': showCurtain ? curtainHeight : '0'}" :class="{'showing': showCurtain}">
            <div
            v-if="showCurtain"
            class="header-content-wrapper"
            :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
                <navigation-header-breadcrumbs
                v-if="!hideBreadcrumbs"
                lightBackground
                :customBreadcrumbs="customBreadcrumbs"
                :nonAuthenticatedFeature="nonAuthenticatedFeature"
                @parentBreadcrumbClicked="emit('parentBreadcrumbClicked')"/>
                <div v-if="showSearchSlot" class="top-search-area">
                    <slot name="top-search-area-slot"></slot>
                </div>
                <div v-if="showStepper" class="top-stepper-area">
                    <slot name="top-stepper-area-slot"></slot>
                </div>
                <tabs-generic v-if="tabs" :tabsList="tabs" @clicked="clickHandler"/>
            </div>
        </div>
        <!-- *********************************************
        *                                                *
        *                   Header Web                	 *
        *                                                *
        ********************************************** -->
        <div v-if="!getMobileAppState && !nonAuthenticatedFeature" class="header-content-wrapper">
            <div class="feature-header-base-top-wrapper">
                <navigation-header-breadcrumbs
                v-if="!hideBreadcrumbs"
                :customBreadcrumbs="customBreadcrumbs"
                :nonAuthenticatedFeature="nonAuthenticatedFeature"
                @parentBreadcrumbClicked="emit('parentBreadcrumbClicked')"/>
                <div v-if="headerButtons && !hideHeaderButtons && !showTopRightAreaSlot" class="buttons-top-wrapper">
                    <button-primary
                    v-for="button in headerButtons"
                    buttonSize="medium"
                    :icon="button.icon || 'plus'"
                    :key="`topHeaderButton${button.id}`"
                    :label="button.name"
                    @clicked="navigateByPath(button.link)"/>
                </div>
                <slot name="accounts-card-slot-area"></slot>
            </div>
            <div v-if="showSearchSlot" class="top-search-area">
                <slot name="top-search-area-slot"></slot>
            </div>
            <div v-if="tabs" class="tabs-and-extra-content-slot-wrapper">
                <tabs-generic :tabsList="tabs" @clicked="clickHandler"/>
                <slot name="tabs-slot-extra-content"></slot>
            </div>
        </div>
        <!-- *********************************************
        *                                                *
        *                   Header Mobile                *
        *                                                *
        ********************************************** -->
        <div v-else class="header-content-wrapper" :class="{'is-mobile-bottom-feature-wrapper': isMobileBottomFeatureWrapper}">
            <slot name="top-mobile-header-slot"></slot>
            <div v-if="!hideBreadcrumbs && !hideBreadcrumbsButShowOnCurtain" class="feature-header-base-top-wrapper">
                <navigation-header-breadcrumbs :customBreadcrumbs="customBreadcrumbs" :nonAuthenticatedFeature="nonAuthenticatedFeature"/>
            </div>
            <div v-if="showSearchSlot && isMobileBottomFeatureWrapper" class="top-search-area mobile-bottom-feature-wrapper">
                    <slot name="top-search-area-slot"></slot>
                </div>
            <div
            v-if="tabs || showTopRightAreaSlot || (showSearchSlot && !isMobileBottomFeatureWrapper)"
            class="bottom-mobile-like-content-wrapper"
            :class="{'with-tabs': tabs || showTopRightAreaSlot, 'white-background': fullWhiteBox }">
                <div v-if="showTopRightAreaSlot || headerButtons" class="accounts-cards-slot-area">
                    <div v-if="headerButtons && !hideHeaderButtons && !showTopRightAreaSlot" class="buttons-top-wrapper">
                    </div>
                    <slot name="accounts-card-slot-area"></slot>
                </div>
                <div v-if="tabs" class="tabs-and-extra-content-slot-wrapper">
                    <tabs-generic :tabsList="tabs" @clicked="clickHandler"/>
                    <slot name="tabs-slot-extra-content"></slot>
                </div>
                <div v-if="showSearchSlot && !isMobileBottomFeatureWrapper" class="top-search-area">
                    <slot name="top-search-area-slot"></slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import NavigationHeaderBreadcrumbs from '@/components/generic/navigation/header/Breadcrumbs'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import TabsGeneric from '@/components/generic/tabs/Generic'
/* Vue */
import { ref, computed, onMounted, useSlots, defineAsyncComponent, watch, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
/* Assets & Helpers */
import { navigateByPath } from '@/router/helpers/navigation'
import { getCurrentMenuEntryTabs, getCurrentMenuEntryTopButtons } from '@/assets/scripts/parsers/menu'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

const { getMenu } = mapGetters(moduleMapper.MENU)
const { getMobileAppState, getAppThemeMode, getAppTheme, getDeviceInUse } = mapGetters()
const { updateStatusBarStyle } = mapActions()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked', 'headerHasTabs', 'customTabClicked', 'parentBreadcrumbClicked'])

/* Props
@nonAuthenticatedFeature {Boolean} prop to apply specific styles for this type of features
@isMobileBottomFeatureWrapper {Boolean} prop to apply specific styles for this type of feature
@fullWhiteBox {Boolean} prop to apply to content var(--secondary-light-4) as background color
@valueScrolled {Number} - value scrolled in our feature
@hideBreadcrumbs {Boolean} - value to trigger the hide of breadcrumbs
@hideBreadcrumbsButShowOnCurtain {Boolean}
@customBreadcrumbs {Object} - if our breadcrumbs are not direct from menu construction based on route, use this object
@customHeaderTabs {Array} - array with custom tabs
@showTopRightAreaSlot {Boolean}
@showSearchSlot {Boolean}
@hideTabs {Boolean} - prop to force tabs to not show, even if route has menu ones
@hideClose {Boolean} - prop to force close button to not show
@customMobileBackIcon {String} - name of custom icon for close mobile button
@centerLogo {Boolean} prop to center on nonAuthenticatedFeatures our logo on center
@hideHeaderButtons {Boolean}
*/
const props = defineProps({
    nonAuthenticatedFeature: Boolean,
    isMobileBottomFeatureWrapper: Boolean,
    fullWhiteBox: Boolean,
    valueScrolled: Number,
    hideBreadcrumbs: Boolean,
    hideBreadcrumbsButShowOnCurtain: Boolean,
    customBreadcrumbs: Object,
    customHeaderTabs: Array,
    showSearchSlot: Boolean,
    showTopRightAreaSlot: Boolean,
    showStepper: Boolean,
    hideTabs: Boolean,
    hideClose: Boolean,
    customMobileBackIcon: String,
    centerLogo: Boolean,
    hideHeaderButtons: Boolean
})

const headerRef = ref('')
const helperForStatusBarHeightRef = ref('')
const MAX_HEIGHT_DIFFERENCE = 63 /* diference between our max/min height */

const logoBaiComponent = computed(() => {
    const icon = getMobileAppState.value ? 'logo-world' : `logo-bai-${getAppTheme.value}`
	return defineAsyncComponent(() => import('@/assets/images/' + icon + '.svg?inline'))
})

/*
* Computed to create a dynamic shadow to our header when we scroll our feature content
* Box-shadow is always there, we will simply manipulate it's opacity to create the ilusion it is appearing on scroll
@returns {Object} Object with styles for our header
*/
const wrapperStyles = computed(() => {
    if (getMobileAppState.value) return null

    let boxShadowAlpha = 0 /* Initial opacity for our shadow */
	const endingShadowOpacity = 0.16 /* ending opacity to help handle the current state of our shadow */

    const auxValue = props.valueScrolled > MAX_HEIGHT_DIFFERENCE ? MAX_HEIGHT_DIFFERENCE : props.valueScrolled
	const currentPercDifference = auxValue * 100 / MAX_HEIGHT_DIFFERENCE

	boxShadowAlpha = currentPercDifference * endingShadowOpacity / 100

    if (getAppThemeMode.value === 'dark') {
        return {
            'box-shadow': `0px 8px 16px rgba(0, 0, 0,${boxShadowAlpha})`
        }
    }
	return {
		'box-shadow': `0px 8px 16px rgba(36, 37, 38,${boxShadowAlpha})`
	}
})

/*
* Computed to validate when we will be showing a "curtain" above content to displayed fixed elements on header
*/
const showCurtain = computed(() => {
    if (!getMobileAppState.value) return false

    let heightToShowCurtain = headerRef.value.scrollHeight

    if (getMobileAppState.value) {
        const statusBarHeight = helperForStatusBarHeightRef.value.offsetHeight
        heightToShowCurtain -= statusBarHeight
    }

    if (!props.hideBreadcrumbs) heightToShowCurtain -= 16

    if (props.showSearchSlot) {
        heightToShowCurtain -= 64 // search.height
    }

    if (props.showStepper) {
        heightToShowCurtain -= 48
    }

    if (tabs.value) {
        heightToShowCurtain -= 48 // Tabs.height
    }

    return props.valueScrolled > heightToShowCurtain
})

const curtainHeight = computed(() => {
    if (!showCurtain.value) return 0

    let curtainHeight = 56
    let statusBarHeight = 0

    if (!props.hideBreadcrumbs) curtainHeight += 16

    if (getMobileAppState.value) {
        statusBarHeight = helperForStatusBarHeightRef.value.offsetHeight
        curtainHeight += statusBarHeight
    }

    if (props.showSearchSlot) {
        if (props.hideBreadcrumbs) curtainHeight = 70 + statusBarHeight
        else curtainHeight += 52
    }

    if (props.showStepper) {
        curtainHeight += 58
    }

    if (tabs.value) {
        curtainHeight += 40 // Tabs.height
    }

    return `${curtainHeight}px`
})

watch(() => props.valueScrolled, (newVal, oldVal) => {
    if (getMobileAppState.value && getDeviceInUse.value.toLowerCase() !== 'desktop' && props.valueScrolled > 0 && getAppThemeMode.value !== 'dark') {
        showCurtain.value ? updateStatusBarStyle('default') : updateStatusBarStyle('lightContent')
    }
})

const currentRoute = useRoute()
const tabs = computed(() => {
    if (props.hideTabs) return null
    if (props.customHeaderTabs) return props.customHeaderTabs
    if (props.customBreadcrumbs) return null
    if (!getMenu.value) return

    const thirdLevelTabs = getCurrentMenuEntryTabs(getMenu.value, currentRoute.path)
    if (!thirdLevelTabs.length) return null

    const getCurrentSelectedTab = thirdLevelTabs.find((tab) => { return currentRoute.path.includes(tab.link) })

    return thirdLevelTabs.map((tab, index) => {
        return {
            ...tab,
            label: tab.name,
            selected: getCurrentSelectedTab ? getCurrentSelectedTab.id === tab.id : index === 0
        }
    })
})

const headerButtons = computed(() => {
    return getCurrentMenuEntryTopButtons(currentRoute.path)
})

/*
* For overlay header on scroll,
* we need to make sure that our slots are not being populated, and if not, remove them with a v-if
*/
const showTopRightButtonMobileSlot = computed(() => {
    return useSlots()['top-right-button-mobile-slot']
})

const clickHandler = $event => {
    if (props.customHeaderTabs && props.customHeaderTabs.length) {
        emit('customTabClicked', $event)
    }

    const tabInfo = tabs.value.find(obj => obj.id === $event)

    if (tabInfo.link) {
        navigateByPath(tabInfo.link)
    }
}

onMounted(() => {
    if (tabs.value) emit('headerHasTabs', tabs.value)
})

onBeforeUnmount(() => {
    updateStatusBarStyle('lightContent')
})
</script>

<style lang="scss" scoped>
.feature-header-base-wrapper {
    width: 100%;
    height: auto;

    flex-shrink: 0;
    z-index: 2;

    .to-help-get-ios-status-bar-height {
        height: 0;
        position: fixed;

        &.mobile-status-bar-android {
            height: var(--android-top-padding);
        }

        &.mobile-status-bar-ios {
            height: constant(safe-area-inset-top);
            height: env(safe-area-inset-top);
        }
    }

    .logo-bai {
        height: 40px;

        position: absolute;
        top: 32px;
        left: 24px;
        flex-shrink: 0;

        &.mobile {
            width: auto;
            height: 32px;

            top: 16px;
            left: 16px;
        }

        &.center-logo {
            left: 50%;
            transform: translateX(-50%);
        }
    }

    .tertiary-button-wrapper {
		position: absolute;
        top: 16px;
		left: 16px;
        z-index: 3;

        &.right-position {
            top: 24px;
            left: unset;
            right: 24px;

            &.mobile {
                top: 16px;
                right: 16px;
            }
        }
	}

    .curtain {
        width: 100%;
        height: 0;

        position: fixed;
        z-index: 2;

        top: 0;
        left: 0;
        background-color: var(--secondary-light-4);
        transition: all .1s linear;

        overflow: hidden;

        &.showing {
            box-shadow: var(--box-shadow-curtain);
        }

        .header-content-wrapper {
            display: flex;
            flex-direction: column;

            gap: 16px;

            .top-stepper-area {
                width: 100%;
                height: fit-content;
                padding: 0 12px;
            }

            .generic-tabs-wrapper {
                margin-left: 16px;
            }
        }
    }

    .header-content-wrapper {
        width: 100%;
        height: auto;
        padding: 16px 24px 0 24px;

        position: relative;

        .feature-header-base-top-wrapper {
            width: 100%;
            height: auto;

            display: flex;
            justify-content: space-between;
        }

        .tabs-cenas {
            width: 100%;
            height: 40px;

            background-color: blue;
        }

        .top-search-area.mobile-bottom-feature-wrapper {
            padding: 0 16px;
            margin-bottom: 24px;
        }
    }

    &:not(.mobile) {
        .feature-header-base-top-wrapper {
            min-height: 72px;
        }

        .tabs-and-extra-content-slot-wrapper {
            width: 100%;

            display: flex;
            align-items: center;
        }

        .top-search-area {
            margin-bottom: 24px;
        }
    }

    &.mobile, &.non-authenticated-feature {
        margin-bottom: -1px;

        &.mobile-status-bar-android {
            padding-top: var(--android-top-padding);

            .tertiary-button-wrapper {
                &.position-left {
                    top: calc(16px + var(--android-top-padding));
                }

                &:not(.position-left) {
                    top: calc(16px + var(--android-top-padding));
                }
            }

            .logo-bai {
                &.mobile {
                    top: calc(16px + var(--android-top-padding));
                }
            }
        }

        &.mobile-status-bar-ios {
            padding-top: constant(safe-area-inset-top);
            padding-top: env(safe-area-inset-top);

            .tertiary-button-wrapper {
                &.position-left {
                    top: calc(16px + constant(safe-area-inset-top));
                    top: calc(16px + env(safe-area-inset-top));
                }

                &:not(.position-left) {
                    top: calc(16px + constant(safe-area-inset-top));
                    top: calc(16px + env(safe-area-inset-top));
                }
            }

            .logo-bai {
                &.mobile {
                    top: calc(16px + constant(safe-area-inset-top));
                    top: calc(16px + env(safe-area-inset-top));
                }
            }
        }

        .curtain {
            .top-search-area {
                width: 100%;
                padding: 0 16px;
            }
        }

        .header-content-wrapper {
            padding: 16px 0 0 0;

            &.mobile-status-bar-android {
                padding-top: calc(16px + var(--android-top-padding));
            }

            &.mobile-status-bar-ios {
                padding-top: calc(16px + constant(safe-area-inset-top));
                padding-top: calc(16px + env(safe-area-inset-top));
            }

            &.is-mobile-bottom-feature-wrapper {
                padding: 0;

                &.mobile-status-bar-android {
                    padding: 0;
                }

                &.mobile-status-bar-ios {
                    padding: 0;
                }
            }

            .feature-header-base-top-wrapper {
                margin-bottom: 24px;
                align-items: center;
            }

            .bottom-mobile-like-content-wrapper {
                width: 100%;
                height: auto;
                padding: 16px 0 0 0;

                display: flex;
                justify-content: flex-end;
                gap: 16px;

                background-color: var(--secondary-light-3);
                border-radius: 16px 16px 0 0;

                &.white-background {
                    padding-bottom: 0;

                    &:after {
                        content: '';
                        width: 100%;
                        height: 16px;
                    }
                }

                &.with-tabs {
                    padding: 16px 0;
                    justify-content: flex-start;
                    flex-direction: column;

                    &.white-background {
                        padding-bottom: 0;
                    }

                    .tabs-and-extra-content-slot-wrapper {
                        width: 100%;
                    }
                }

                .accounts-cards-slot-area {
                    width: 100%;
                    padding: 0 16px;

                    display: flex;
                    justify-content: flex-end;
                }

                .top-search-area {
                    width: 100%;
                    padding: 0 16px;
                    margin-bottom: 16px;
                }
            }
        }

        &.non-authenticated-feature {
            &:not(.mobile) {
                .bottom-mobile-like-content-wrapper {
                    padding: 24px 24px 0 24px;

                    &.with-tabs {
                        padding-bottom: 0;
                    }

                    &.white-background {
                        &:after {
                            border-radius: 16px 16px 0 0;
                            background-color: var(--secondary-light-4);
                        }
                    }
                }
            }
        }
    }
}
</style>
